import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Activity, TrendingUp, ShieldCheck, AlertTriangle, Wallet, Scale, Landmark, Wand2, ArrowLeftRight } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useCurrency } from "@/lib/currency";

export default function FinancialHealth() {
  const [overview, setOverview] = useState(null);
  const [settle, setSettle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [matching, setMatching] = useState(false);
  const { display, other, rate, toggle, fmt } = useCurrency({ base: "SBD", other: "AUD" });

  const load = async () => {
    const [ov, s] = await Promise.all([
      api.get("/money/overview"),
      api.get("/money/settlements"),
    ]);
    setOverview(ov.data);
    setSettle(s.data.summary);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const runMatch = async () => {
    setMatching(true);
    try {
      const r = await api.post("/money/reconciliation/match?window_days=3&amount_tolerance_cents=50");
      const { matched, fee_variance, missing_deposit, duplicate } = r.data;
      toast.success(`${matched} matched · ${fee_variance} variance · ${missing_deposit} missing · ${duplicate} duplicate`);
      load();
    } finally { setMatching(false); }
  };

  if (loading || !overview || !settle) return <div className="p-8 text-sm text-surface-onVariant">Assessing financial health…</div>;

  // Compute key ratios
  const netPos = overview.net_position_cents;
  const income = overview.month.income_cents;
  const expense = overview.month.expense_cents;
  const netCash = overview.month.net_cents;
  const savingsRate = overview.month.savings_rate;
  const matchedPct = settle.count ? Math.round((settle.matched / settle.count) * 100) : 100;
  const exceptionsPct = settle.count ? Math.round((settle.exceptions / settle.count) * 100) : 0;
  const daysOfRunway = expense > 0 ? Math.floor((netPos / (expense / 30))) : 999;
  const feeToGross = settle.gross_cents ? ((settle.fees_cents / settle.gross_cents) * 100).toFixed(2) : "0.00";

  // Health "ring" score: composite of reconciliation, cash runway, savings rate
  const reconScore = matchedPct;
  const runwayScore = Math.min(100, Math.round((daysOfRunway / 90) * 100));
  const savingsScore = Math.min(100, Math.max(0, Math.round(savingsRate + 20) * 2));
  const composite = Math.round((reconScore * 0.4 + runwayScore * 0.4 + savingsScore * 0.2));
  const grade = composite >= 85 ? "Excellent" : composite >= 70 ? "Strong" : composite >= 50 ? "Watch" : "At risk";
  const gradeTone = composite >= 85 ? "text-[#0F5027]" : composite >= 70 ? "text-primary" : composite >= 50 ? "text-yellow-700" : "text-danger";

  return (
    <div className="animate-fade-in-up space-y-6" data-testid="financial-health-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-4xl sm:text-5xl font-light tracking-tight text-surface-on">Financial Health</h1>
          <p className="text-base text-surface-onVariant mt-1">Business viability at a glance · Bloom &amp; Co Retail</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="currency-toggle" onClick={toggle}
            className="inline-flex items-center gap-2 rounded-full bg-surface-container hover:bg-primary/8 text-surface-on text-sm font-medium px-4 py-2 transition-colors border border-outline-variant"
            title={rate ? `1 SBD = ${rate.toFixed(4)} AUD` : ""}>
            <ArrowLeftRight className="w-4 h-4" />
            View in {other}
          </button>
          <button data-testid="run-match-btn-health" onClick={runMatch} disabled={matching} className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground text-sm font-medium px-4 py-2 hover:brightness-110 active:scale-[0.98] disabled:opacity-40 transition-[transform,filter] duration-200 ease-md">
            <Wand2 className="w-4 h-4" /> {matching ? "Reconciling…" : "Run reconciliation"}
          </button>
        </div>
      </div>

      {/* Ring hero */}
      <div className="rounded-hero bg-surface-container p-8 flex flex-col md:flex-row items-center gap-8">
        <Ring value={composite} />
        <div className="flex-1 min-w-0">
          <div className={cn("text-3xl font-light tracking-tight", gradeTone)}>{grade}</div>
          <p className="text-sm text-surface-onVariant mt-1 max-w-md">
            {grade === "Excellent" && "Every settlement is landing on time, cash is stacking, and fees stay low."}
            {grade === "Strong" && "Solid position. Watch the exception queue and keep the fee ratio in check."}
            {grade === "Watch" && "Cash is thin or reconciliation is falling behind — investigate exceptions this week."}
            {grade === "At risk" && "Immediate attention needed. Cash runway is short or too many settlements are unmatched."}
          </p>
          <div className="grid grid-cols-3 gap-3 mt-5 max-w-xl">
            <Sub label="Recon" value={`${reconScore}%`} />
            <Sub label="Runway" value={`${daysOfRunway}d`} />
            <Sub label="Savings" value={`${savingsRate}%`} />
          </div>
        </div>
      </div>

      {/* Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card icon={<Wallet />} tone="primary" label="Net position" value={fmt(netPos)} sub={`${overview.accounts.length} account${overview.accounts.length !== 1 ? "s" : ""}`} />
        <Card icon={<TrendingUp />} tone="positive" label="Cashflow (mo)" value={fmt(netCash)} sub={`${fmt(income)} in · ${fmt(expense)} out`} />
        <Card icon={<Scale />} tone="primary" label="Reconciliation" value={`${matchedPct}%`} sub={`${settle.matched}/${settle.count} settlements matched`} highlight />
        <Card icon={<AlertTriangle />} tone={settle.exceptions > 0 ? "danger" : "muted"} label="Exceptions" value={settle.exceptions} sub={settle.exceptions === 0 ? "All clear" : `${exceptionsPct}% flagged`} />
        <Card icon={<Landmark />} tone="muted" label="Fee ratio" value={`${feeToGross}%`} sub="Merchant fees / gross" />
        <Card icon={<ShieldCheck />} tone={daysOfRunway > 60 ? "positive" : daysOfRunway > 30 ? "primary" : "danger"} label="Days of runway" value={`${daysOfRunway}d`} sub="At current burn" />
        <Card icon={<Activity />} tone="muted" label="Card sales (mo)" value={fmt(settle.gross_cents)} sub={`${settle.count} settlement rows`} />
        <Card icon={<AlertTriangle />} tone={settle.unmatched > 5 ? "danger" : "muted"} label="Unmatched" value={settle.unmatched} sub="Awaiting bank deposit" />
      </div>

      {/* Cashflow chart */}
      <div className="rounded-hero bg-surface-container p-6">
        <h3 className="text-lg font-medium text-surface-on mb-4">6-month cashflow trend</h3>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={overview.cashflow_6mo}>
            <CartesianGrid strokeDasharray="3 3" stroke="#CAC4D0" vertical={false} />
            <XAxis dataKey="month" stroke="#49454F" style={{ fontSize: 11 }} />
            <YAxis stroke="#49454F" style={{ fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 100000)}k`} />
            <Tooltip formatter={(v) => fmt(v)} contentStyle={{ backgroundColor: "#FFFBFE", border: "1px solid #CAC4D0", borderRadius: 12 }} />
            <Line type="monotone" dataKey="income_cents" name="Income" stroke="#0F5027" strokeWidth={2.5} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="expense_cents" name="Expense" stroke="#B3261E" strokeWidth={2.5} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Ring({ value }) {
  const size = 160, stroke = 14;
  const radius = (size - stroke) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ * (1 - value / 100);
  const color = value >= 85 ? "#0F5027" : value >= 70 ? "#6750A4" : value >= 50 ? "#B58E00" : "#B3261E";
  return (
    <div className="relative w-40 h-40 shrink-0" data-testid="fh-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} stroke="#E7E0EC" strokeWidth={stroke} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={radius} stroke={color} strokeWidth={stroke} fill="none"
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 700ms cubic-bezier(0.2, 0, 0, 1)" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-5xl font-light tabular-nums" style={{ color }}>{value}</div>
        <div className="text-[10px] tracking-widest uppercase text-surface-onVariant">Score</div>
      </div>
    </div>
  );
}

function Card({ icon, label, value, sub, tone = "muted", highlight = false }) {
  const toneCls = tone === "primary" ? "text-primary" : tone === "positive" ? "text-[#0F5027]" : tone === "danger" ? "text-danger" : "text-surface-on";
  return (
    <div className={cn("rounded-hero bg-surface-container p-5", highlight && "ring-2 ring-primary/30")}>
      <div className={cn("w-9 h-9 rounded-2xl bg-surface flex items-center justify-center", toneCls)}>
        {icon}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-surface-onVariant mt-3">{label}</div>
      <div className={cn("text-2xl font-medium tabular-nums mt-1", toneCls)}>{value}</div>
      <div className="text-xs text-surface-onVariant mt-1 truncate">{sub}</div>
    </div>
  );
}

function Sub({ label, value }) {
  return (
    <div className="rounded-2xl bg-surface p-3">
      <div className="text-[10px] uppercase tracking-widest text-surface-onVariant">{label}</div>
      <div className="text-lg font-medium tabular-nums text-surface-on mt-1">{value}</div>
    </div>
  );
}
